import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

export type MgVisualState = 'dull' | 'polished' | 'burning' | 'ash';

interface MagnesiumRibbonProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** Visual state of the ribbon material. */
  state?: MgVisualState;
  /** 0..1 — interpolates between dull and polished surface during sanding. */
  polishProgress?: number;
  /** Whether to render as a coiled-up ribbon (true) or flat strip (false). */
  coiled?: boolean;
  /** Length in cm — affects the size of the flat strip or coil radius. */
  lengthCm?: number;
}

/**
 * Magnesium ribbon — a thin shiny strip of metallic magnesium that the
 * student polishes with sandpaper and then ignites in air. Four visual
 * states:
 *  - dull: oxide-coated grey
 *  - polished: bright shiny silver (after sanding)
 *  - burning: bright white emissive (during combustion)
 *  - ash: white powder pile (after combustion)
 */
export function MagnesiumRibbon({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  state = 'dull',
  polishProgress = 0,
  coiled = false,
  lengthCm = 10,
}: MagnesiumRibbonProps) {
  const matRef = useRef<THREE.MeshStandardMaterial>(null);
  const groupRef = useRef<THREE.Group>(null);

  useFrame((s) => {
    if (matRef.current) {
      if (state === 'burning') {
        const pulse = 0.75 + Math.sin(s.clock.elapsedTime * 22) * 0.2;
        matRef.current.emissiveIntensity = pulse;
      }
    }
  });

  if (state === 'ash') {
    return (
      <group position={position} rotation={rotation} scale={scale}>
        <mesh position={[0, 0.006, 0]}>
          <coneGeometry args={[0.04, 0.012, 24]} />
          <meshStandardMaterial color="#f9f9fa" roughness={0.95} metalness={0.02} />
        </mesh>
      </group>
    );
  }

  const t = polishProgress;
  const dullColor = new THREE.Color('#8d949c');
  const shinyColor = new THREE.Color('#dee4ea');
  const surfaceColor = (state === 'dull' && t === 0) ? '#8d949c'
    : state === 'burning' ? '#ffffff'
    : `#${dullColor.clone().lerp(shinyColor, state === 'polished' ? 1 : t).getHexString()}`;
  const metalness = 0.4 + (state === 'polished' ? 0.55 : t * 0.55);
  const roughness = 0.6 - (state === 'polished' ? 0.45 : t * 0.45);

  if (coiled) {
    const coilRadius = 0.018 + 0.004 * Math.max(0, (lengthCm - 5) / 10);
    return (
      <group ref={groupRef} position={position} rotation={rotation} scale={scale}>
        <mesh castShadow>
          <torusGeometry args={[coilRadius, 0.0035, 8, 28]} />
          <meshStandardMaterial
            ref={matRef}
            color={surfaceColor}
            metalness={metalness}
            roughness={roughness}
            emissive={state === 'burning' ? '#ffffff' : '#000000'}
            emissiveIntensity={state === 'burning' ? 1.0 : 0}
          />
        </mesh>
        <mesh position={[0, 0.004, 0]} castShadow>
          <torusGeometry args={[coilRadius * 0.7, 0.003, 8, 24]} />
          <meshStandardMaterial
            color={surfaceColor}
            metalness={metalness}
            roughness={roughness}
            emissive={state === 'burning' ? '#ffffff' : '#000000'}
            emissiveIntensity={state === 'burning' ? 0.9 : 0}
          />
        </mesh>
      </group>
    );
  }

  const stripLength = 0.01 * lengthCm;

  return (
    <group ref={groupRef} position={position} rotation={rotation} scale={scale}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[stripLength, 0.001, 0.008]} />
        <meshStandardMaterial
          ref={matRef}
          color={surfaceColor}
          metalness={metalness}
          roughness={roughness}
          emissive={state === 'burning' ? '#ffffff' : '#000000'}
          emissiveIntensity={state === 'burning' ? 1.0 : 0}
        />
      </mesh>
    </group>
  );
}

export default MagnesiumRibbon;
